import { MAIN_STYLE, MAIN_STYLE_GROUPS, PRESS_EVENTS, SELECTION_EVENTS } from '../shared.js'
export const tabview = {
    parts: {
        main: {
            label: 'Main',
            exportSuffix: '',
            order: 0,
            propertyGroups: [...MAIN_STYLE_GROUPS],
            properties: { ...MAIN_STYLE },
        },
        indicator: {
            label: 'Indicator',
            exportSuffix: '10',
            order: 1,
            properties: {
                bg_color: { type: 'color' },
                // refr_indic_size() reads lv_obj_get_style_size(tabview,
                // LV_TABVIEW_PART_INDIC) to set the indicator bar's thickness
                // (theme default HASP_DPX(5), lv_theme_hasp.c:976) — was
                // completely unexposed. See FIRMWARE-PARITY-AUDIT.md's
                // "tabview" section.
                size:     { type: 'int', min: 0 },
            },
        },
        // NOTE: this part is named 'items' and mapped to exportSuffix '30', but per
        // firmware's real TABVIEW part-routing (hasp_attribute.cpp's part-suffix
        // switch) suffix 30 is LV_TABVIEW_PART_TAB_BG, i.e. this should really be
        // named 'items_bg' (see FIRMWARE-PARITY-AUDIT.md's "tabview" section).
        // The rename is DEFERRED: HaspTabview.js hardcodes this.style('items') /
        // this.onStyle('items', ...) in multiple places (HaspTabview.js:54-72,129)
        // to read this exact part by name, so renaming it here alone would
        // silently break the live canvas rendering (indicator line color, tab-bar
        // background fill/border/radius) until HaspTabview.js is updated to match
        // — that widget-code change is out of scope for this schema-only pass.
        // Do not rename until HaspTabview.js is migrated in the same change.
        items: {
            label: 'Items',
            exportSuffix: '30',
            order: 2,
            properties: {
                bg_color:     { type: 'color' },
                border_color: { type: 'color' },
                border_width: { type: 'int', min: 0 },
                radius:       { type: 'int', min: 0, max: 999 },
            },
        },
        // Real LVGL/firmware part is LV_TABVIEW_PART_TAB_BTN (suffix 40) — the
        // actual clickable, genuinely-checkable tab button; lv_tabview_set_tab_act
        // marks the active tab checked (lv_tabview.c:434,837), and the firmware
        // theme richly differentiates pressed/checked/focused/edited colors for it
        // (lv_theme_hasp.c:964-970). Named 'item_buttons' rather than the audit's
        // literal suggestion of 'items' — that name is already taken by the
        // suffix-30 part above (whose rename to 'items_bg' is deferred, see its
        // comment), and 'item_buttons' is this codebase's existing, consistent
        // naming convention for exactly this suffix-40/BTN-type part (see
        // dropdown.js and msgbox.js, both of which already use 'item_buttons' at
        // exportSuffix '40' for the analogous part).
        item_buttons: {
            label: 'Item Buttons',
            exportSuffix: '40',
            order: 3,
            properties: {
                bg_color:     { type: 'color' },
                text_color:   { type: 'color' },
                border_width: { type: 'int', min: 0 },
                border_color: { type: 'color' },
            },
        },
        // Real LVGL/firmware part is LV_TABVIEW_PART_BG_SCROLLABLE (suffix 50) —
        // the container holding all tab-content pages side by side. Minimal
        // properties only (bg_color + padding). Per this codebase's
        // exportSpecial convention, the `padding` shorthand is ONLY handled by
        // ObjectExport.js for part names 'main'/'knob'/'item_buttons' — since
        // 'selected' isn't one of those, direct pad_* property names are used
        // here instead so they actually get exported.
        selected: {
            label: 'Selected',
            exportSuffix: '50',
            order: 4,
            properties: {
                bg_color:  { type: 'color' },
                pad_top:    { type: 'int', min: 0, label: 'pad top' },
                pad_bottom: { type: 'int', min: 0, label: 'pad bottom' },
                pad_left:   { type: 'int', min: 0, label: 'pad left' },
                pad_right:  { type: 'int', min: 0, label: 'pad right' },
            },
        },
    },

    properties: {
        x: { type: 'int' }, y: { type: 'int' },
        width: { type: 'int', min: 0 }, height: { type: 'int', min: 0 },
        min_width: { type: 'int', min: 0 }, min_height: { type: 'int', min: 0 },
        enabled: { type: 'bool' }, hidden: { type: 'bool' },
        val:              { type: 'int',  min: 0 },
        text:             { type: 'string', readOnly: true },
        count:            { type: 'int',  readOnly: true },
        btn_pos:          { type: 'enum', values: [0, 1, 2, 3, 4] },
        swipe:            { type: 'bool' },
        text_font:        { type: 'int', min: 8 },
        text_font_family: { type: 'string', internal: true },
        // Font Family selection for the tab buttons' text — see label.js's
        // `font` property comment for the full wire-format semantics.
        font: { type: 'resource', resourceType: 'font', label: 'Font Family', inline: true },
    },

    defaults: {
        properties: {
            x: 0, y: 0, width: 200, height: 200, min_width: 200, min_height: 100,
            enabled: true, hidden: false,
            btn_pos: 1,
            font: '',
        },
    },
    // 'checked' added for the new item_buttons (TAB_BTN) part — the tab button
    // is genuinely checkable in firmware (lv_tabview_set_tab_act marks the
    // active tab checked, lv_tabview.c:434,837), enabling active-vs-inactive
    // tab styling. See FIRMWARE-PARITY-AUDIT.md's "tabview" section.
    states: ['pressed', 'disabled', 'checked'],
    editorCapabilities:  ['can_transform', 'can_copy', 'has_objectmenu'],
    runtimeCapabilities: [],

    events: SELECTION_EVENTS,
    bindings: { properties: {}, actions: { hass_services: [] }, events: {} },
}

export const tab = {
    parts: {},

    properties: {
        text: { type: 'string' },
    },

    defaults: { properties: { text: '' } },

    editorCapabilities:  ['no_gridsnap', 'has_objectmenu'],
    runtimeCapabilities: [],

    events: PRESS_EVENTS,
    bindings: { properties: {}, actions: {}, events: {} },
}
